import { useMemo, useState } from 'react';
import {
  Search, ChevronRight, ChevronDown, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import {
  buildPalette, filterPalette, groupPalette, PALETTE_DRAG_TYPE,
} from '@/lib/workflowPalette';
import { iconFor } from '@/components/workflow/nodeIcons';

/**
 * The builder's component palette.
 *
 * Entries come from lib/workflowPalette, which turns this install's real tools, models and
 * connections into droppable pre-configured nodes — so the panel is a catalogue of what you
 * can actually do here, not a list of six abstract node types.
 *
 * Drag uses the HTML5 API rather than a drag-and-drop library: the canvas is the only drop
 * target and the payload is one JSON string, which is exactly what dataTransfer is for.
 */

function EntryRow({ entry, onAdd }) {
  const Icon = iconFor(entry.icon);
  const isAgent = entry.category === 'Agents';
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(PALETTE_DRAG_TYPE, JSON.stringify(entry));
        e.dataTransfer.effectAllowed = 'copy';
      }}
      onClick={() => onAdd(entry)}
      title={entry.description}
      className="w-full flex items-start gap-2 px-2 py-1.5 rounded text-left hover:bg-[var(--app-border)]/30 cursor-grab active:cursor-grabbing"
    >
      <Icon size={14} className={`mt-0.5 shrink-0 ${isAgent ? 'text-purple-600 dark:text-purple-400' : 'text-secondary-500'}`} />
      <span className="min-w-0">
        <span className={`block text-xs font-medium truncate ${isAgent ? 'text-purple-900 dark:text-purple-200' : ''}`}>{entry.label}</span>
        {entry.description && (
          <span className="block text-[10px] text-secondary-500 truncate">{entry.description}</span>
        )}
      </span>
    </button>
  );
}

/** Hand-rolled rather than twreact's Accordion: the group needs to force itself open while
 *  a search is active, which a self-managing accordion cannot be told to do. */
function CategoryGroup({ group, onAdd, forceOpen }) {
  const [open, setOpen] = useState(true);
  const expanded = forceOpen || open;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <div className="border-b border-[var(--app-border)] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 px-2 py-2 text-sm font-medium hover:bg-[var(--app-border)]/20"
      >
        <Chevron size={14} className="text-secondary-500" />
        <span className="flex-1 text-left truncate">{group.category}</span>
        <span className="text-[10px] text-secondary-500">{group.entries.length}</span>
      </button>
      {expanded && (
        <div className="pb-1 px-1 space-y-0.5">
          {group.entries.map((entry) => <EntryRow key={entry.key} entry={entry} onAdd={onAdd} />)}
        </div>
      )}
    </div>
  );
}

export default function NodePalette({
  nodeCatalog, toolOptions, providers, connections, modelsByProvider, agents, connectors, onAdd, collapsed, onToggleCollapsed,
}) {
  const [query, setQuery] = useState('');
  const entries = useMemo(
    () => buildPalette({
      nodeCatalog, toolOptions, providers, connections, modelsByProvider, agents, connectors,
    }),
    [nodeCatalog, toolOptions, providers, connections, modelsByProvider, agents, connectors],
  );
  const groups = useMemo(() => groupPalette(filterPalette(entries, query)), [entries, query]);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggleCollapsed}
        title="Show components"
        className="h-full w-9 shrink-0 flex items-start justify-center pt-3 rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] text-secondary-500 hover:text-primary-600"
      >
        <PanelLeftOpen size={16} />
      </button>
    );
  }

  return (
    <div className="h-full w-64 shrink-0 flex flex-col rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] overflow-hidden">
      <div className="px-2 py-1.5 border-b border-[var(--app-border)] space-y-1.5">
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold flex-1">Components</span>
          <button type="button" onClick={onToggleCollapsed} title="Hide components" className="p-1 rounded text-secondary-500 hover:text-primary-600">
            <PanelLeftClose size={14} />
          </button>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-secondary-500" />
          <input
            className="w-full border rounded-md pl-7 pr-2 py-1 text-xs bg-[var(--app-surface)]"
            placeholder="Search components…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <p className="text-[10px] text-secondary-500">
          {entries.length} component{entries.length === 1 ? '' : 's'} available — drag onto the canvas, or click to add.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {groups.length === 0 ? (
          <p className="p-3 text-xs text-secondary-500">Nothing matches “{query}”.</p>
        ) : groups.map((group) => (
          <CategoryGroup key={group.category} group={group} onAdd={onAdd} forceOpen={!!query} />
        ))}
      </div>
    </div>
  );
}
